// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ClickableCard.tsx
 * @input Uses Card, useClickableContainer, StyleX
 * @output Exports ClickableCard component and ClickableCardProps
 * @position Interactive card for navigation or action targets
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/ClickableCard/ClickableCard.doc.mjs (props table, features)
 * - /packages/core/src/ClickableCard/index.ts (exports if types change)
 * - /apps/storybook/stories/ClickableCard.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Card/ClickableCardShowcase.tsx (showcase block)
 * - /packages/cli/templates/blocks/components/Card/ClickableCardWithNestedButton.tsx (block)
 *
 * Composes Card for all visual styling (radius, padding, variants,
 * container tokens, theming). Adds an interactive wrapper with
 * useClickableContainer for safe nested interactive elements.
 *
 * A hidden <button> or <a> inside the card provides the accessible role,
 * label, and focus ring — the card surface itself has no role/tabIndex.
 * This gives screen readers a real interactive element to announce while
 * keeping the visual hover/active overlay on the full card.
 *
 * For static display, use Card.
 * For toggle selection, use SelectableCard.
 */
import { useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { borderVars, colorVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { Card } from "../Card/Card.js";
import { useClickableContainer } from "../hooks/useClickableContainer.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Styles — only the interactive layer, Card handles everything else
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  interactive: {
    kVAEAm: "x1n2onr6",
    kkrTdU: "x1ypdohk",
    kybGjl: "x1hl2dhg",
    kMwMTN: "x1heor9g",
    kInvED: "x1hl8ikr",
    $$css: true
  },
  focusWithin: {
    kRYL1X: "x1irc7jg",
    koJ47v: "xdjuwb3",
    $$css: true
  },
  overlay: {
    k5JduY: "x1s928wv",
    kwXMNM: "x1j6awrg",
    kv0HGH: "xarstr8",
    kloYau: "x2q1x1w",
    kRicXK: "x1ywzrc5",
    kPNhGg: "x97pup0",
    kA8PQs: "x1dlmc9c",
    ks3ayO: "xyhc2n1",
    kAcZsS: "xotisz4",
    $$css: true
  },
  hoverOnPointer: {
    kJs8I2: "x1vwwndy",
    $$css: true
  },
  borderless: {
    kMzoRj: "xc342km",
    $$css: true
  },
  bordered: {
    kVAM5u: "x14i3s5s",
    kZCmMZ: "xs19ii7",
    kwRFfy: "x12frdag",
    kLKAdn: "x1nex4ik",
    kGO01o: "xbv1mwh",
    k1ekBW: "xshfolx",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  borderedHoverOnPointer: {
    kbt25U: "x1ww4t2b",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    kSiTet: "xbyyjgo",
    $$css: true
  }
};

// =============================================================================
// Props
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * An interactive card that acts as a single navigation or action target.
 *
 * Composes Card for visual styling and adds an interactive layer
 * with useClickableContainer. Nested interactive elements (buttons,
 * links, inputs) work independently — clicking them does NOT trigger
 * the card's onClick or navigation.
 *
 * A visually-hidden <button> or <a> inside the card provides the
 * accessible role and label. The card surface is a plain <div> —
 * no role or tabIndex on the container.
 *
 * @compositionHint Use for cards that navigate to a detail page or trigger an action.
 * For toggle selection cards, use SelectableCard instead.
 * Nest Button or other interactive elements freely inside — they won't conflict.
 *
 * @example
 * ```
 * <ClickableCard label="Settings" href="/settings">
 *   <Text type="body" weight="bold">Settings</Text>
 *   <Text type="supporting" color="secondary">Manage your preferences</Text>
 * </ClickableCard>
 * ```
 *
 * @example
 * ```
 * <ClickableCard label="Open modal" onClick={() => setShowModal(true)}>
 *   <Text type="body">Click anywhere to open</Text>
 *   <Button label="Other action" onClick={handleOther} />
 * </ClickableCard>
 * ```
 */
export function ClickableCard({
  label,
  onClick: onClickProp,
  onMouseUp: onMouseUpProp,
  href,
  target,
  isDisabled = false,
  children,
  padding,
  variant = 'default',
  width,
  height,
  maxWidth,
  ref,
  xstyle: xstyleProp,
  className: classNameProp,
  style,
  ...props
}) {
  const containerRef = useRef(null);
  const interactiveRef = useRef(null);
  const LinkComponent = useLinkComponent();
  const {
    onClick,
    onMouseUp
  } = useClickableContainer({
    containerRef,
    interactiveRef,
    onClick: onClickProp,
    href,
    target,
    disabled: isDisabled
  });
  const handleMouseUp = onMouseUpProp ? e => {
    onMouseUp(e);
    onMouseUpProp(e);
  } : onMouseUp;
  const isLink = href != null;

  // Only the `default` variant has a visible border. Card draws a transparent
  // 1px border on every other variant purely to avoid layout jitter; we drop
  // it here so the hover overlay covers the full box with no untinted ring.
  const hasBorder = variant === 'default';
  return /*#__PURE__*/_jsxs(Card, {
    ref: mergeRefs(ref, containerRef),
    width: width,
    height: height,
    maxWidth: maxWidth,
    padding: padding,
    variant: variant,
    ...mergeProps(themeProps('clickable-card', {
      variant
    }), {
      className: classNameProp,
      style
    }),
    xstyle: [styles.interactive, styles.focusWithin, hasBorder ? styles.bordered : styles.borderless, !isDisabled && styles.overlay, !isDisabled && styles.hoverOnPointer, !isDisabled && hasBorder && styles.borderedHoverOnPointer, isDisabled && styles.disabled, xstyleProp],
    onClick: !isDisabled ? onClick : undefined,
    onMouseUp: !isDisabled ? handleMouseUp : undefined,
    ...props,
    children: [isLink ? /*#__PURE__*/_jsx(LinkComponent, {
      ref: interactiveRef,
      href: href,
      target: target,
      "aria-label": label,
      "aria-disabled": isDisabled || undefined,
      tabIndex: isDisabled ? -1 : 0,
      ...{
        className: "x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr xzpqnlu xuxw1ft xc342km"
      }
    }) : /*#__PURE__*/_jsx("button", {
      ref: interactiveRef,
      type: "button",
      "aria-label": label,
      disabled: isDisabled,
      onClick: onClickProp,
      ...{
        className: "x10l6tqk x1i1rx1s xjm9jq1 x1717udv xkdpibf xb3r6kr xzpqnlu xuxw1ft xc342km"
      }
    }), children]
  });
}
ClickableCard.displayName = 'ClickableCard';